// Where a finished sign-in should put the human back — the PRODUCER half of
// `apps/api/src/auth-return-to.ts`, which is the one validator all three login
// legs share.
//
// <App/> renders <Login/> at ANY path when there is no session (#475), so a
// signed-out invitee who opens `/invite?token=inv_…` sees the login screen AT
// that path. Every leg used to land back on the bare dashboard origin, which
// dropped the single-use invitation token and dead-ended the funnel.
//
// The API's set is NAMED, not "same origin": an ABSOLUTE url whose origin is the
// dashboard's, or the relative `/oauth/authorize` reference it reserves for its
// own sign-in page. Everything else — a bare path, a foreign origin — is a 400,
// which is a sign-in button that does not work. So the target is built from
// `window.location.origin` and simply omitted when there is nothing to say:
// absent and blank both read as "no target" and land on the dashboard root.

/** The parameter every login leg reads its target from (the API's `RETURN_TO_PARAM`). */
const RETURN_TO_PARAM = 'return_to';

/**
 * Base for parsing a reference that may be relative: `API_BASE_URL` is empty
 * same-origin in dev and in tests, so `/auth/github/login` names no origin of its
 * own. RFC 2606 reserves `.invalid`, so nothing can really be on it — which is
 * what makes "landed on the probe ⇒ the input was relative" unambiguous.
 */
const PROBE_ORIGIN = 'https://login-leg.invalid';

/**
 * The API's `OAUTH_AUTHORIZE_PATH` (`apps/api/src/routes/oauth-metadata.ts`),
 * repeated here because apps never import each other — the same way this file
 * already names `/auth/github/login`.
 */
const API_AUTHORIZE_PATH = '/oauth/authorize';

/**
 * Paths a completed sign-in must not be sent back to: the login screen itself
 * (a bounce — and the chooser deep-links `/login/email` + `/login/sso`) and the
 * root, which is already where a bare sign-in lands.
 */
function isBounce(pathname: string): boolean {
  return pathname === '/' || pathname === '/login' || pathname.startsWith('/login/');
}

/**
 * The API's signed-out authorize page bounces a connector's human to the login
 * chooser with the authorize request in `return_to` (`apps/api/src/oauth/flow.ts`
 * `signedOutAuthorize`), because the chooser is the only screen that offers all
 * three legs. Relaying that is the ONLY way a login path can carry a target:
 * derived from the location there is none, since returning to the chooser is a
 * bounce.
 *
 * RELAYED, NOT TRUSTED, AND NARROWER THAN THE API'S SET. The one value ever
 * handed here is that relative `/oauth/authorize?…` reference, so it is the one
 * value read. Anything else is ignored rather than forwarded: the API answers a
 * non-member with a 400 — a sign-in button that does not work — and a
 * `?return_to=` crafted onto a link to our own login screen must not be able to
 * produce one.
 *
 * Parsed, never prefix-matched. `//evil.test/oauth/authorize` is a relative
 * reference by shape and cross-origin once the browser resolves it, so a
 * `startsWith` here would forward exactly the value the API's set exists to
 * refuse.
 */
function handedAuthorizeTarget(search: string): string | null {
  const handed = new URLSearchParams(search).get(RETURN_TO_PARAM);
  if (!handed) return null;
  let url: URL;
  try {
    url = new URL(handed, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN || url.pathname !== API_AUTHORIZE_PATH) return null;
  // Re-emitted RELATIVE (never `url.toString()`, which would carry the probe
  // origin), so the API resolves it against its own callback host.
  return `${url.pathname}${url.search}`;
}

/**
 * Where a login round-trip should return to — the dashboard-origin URL the human
 * was on, the authorize request the API handed the login screen, or `null` when
 * there is nothing worth returning to.
 */
export function currentReturnTo(
  loc: { origin: string; pathname: string; search: string } = window.location,
): string | null {
  if (isBounce(loc.pathname)) return handedAuthorizeTarget(loc.search);
  return `${loc.origin}${loc.pathname}${loc.search}`;
}

/**
 * Append `return_to` to a login-leg URL when there is a target.
 *
 * Composed through `URL`, never concatenated: `orgSsoLoginUrl` and a configured
 * `PUBLIC_API_URL` may both carry a query already, and a `?` spliced onto one is
 * a leg the API cannot parse.
 */
export function withReturnTo(url: string, returnTo?: string | null): string {
  if (!returnTo) return url;
  const leg = new URL(url, PROBE_ORIGIN);
  leg.searchParams.set(RETURN_TO_PARAM, returnTo);
  return leg.origin === PROBE_ORIGIN ? `${leg.pathname}${leg.search}${leg.hash}` : leg.toString();
}
